import { useQuery } from "@tanstack/react-query";
import { TrendingUp, AlertTriangle, ShieldCheck, BarChart3, Activity } from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { formatDate } from "@/lib/utils";

export function Analytics() {
  const trendQ = useQuery({ queryKey: ["analytics", "trend"], queryFn: () => endpoints.analytics.trend() });
  const topQ = useQuery({ queryKey: ["analytics", "top"], queryFn: () => endpoints.analytics.topResources(8) });
  const jobsQ = useQuery({ queryKey: ["analytics", "jobs"], queryFn: () => endpoints.analytics.jobsRate() });
  const scadenzeQ = useQuery({ queryKey: ["scadenze", "certificati"], queryFn: () => endpoints.scadenze.certificati(90) });

  const trendItems = trendQ.data?.items ?? [];
  const trendMax = Math.max(1, ...trendItems.map((d) => d.count));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground">Trend, top risorse, tasso di successo, scadenzario</p>
      </div>

      {/* Cards metriche principali */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<Activity className="h-5 w-5" />}
          label="Job Tot."
          value={jobsQ.isError ? "—" : (jobsQ.data?.total ?? 0)}
          tone="muted"
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          label="Successo"
          value={jobsQ.data?.success_rate != null ? `${(jobsQ.data.success_rate * 100).toFixed(1)}%` : "—"}
          tone="success"
        />
        <KpiCard
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Cert. in scadenza"
          value={scadenzeQ.data?.summary.warning ?? 0}
          tone={scadenzeQ.data?.summary.warning ? "warning" : "muted"}
        />
        <KpiCard
          icon={<ShieldCheck className="h-5 w-5" />}
          label="Cert. scaduti"
          value={scadenzeQ.data?.summary.expired ?? 0}
          tone={scadenzeQ.data?.summary.expired ? "danger" : "muted"}
        />
      </div>

      {/* Trend bar chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-5 w-5" /> Richieste — ultimi 30 giorni
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendQ.isLoading && <CenteredLoader />}
          {trendQ.error && <ErrorState error={trendQ.error} />}
          {trendItems.length === 0 && !trendQ.isLoading && <EmptyState title="Nessuna attività" />}
          {trendItems.length > 0 && (
            <div className="flex h-40 items-end gap-1">
              {trendItems.map((d) => {
                const h = Math.max(2, Math.round((d.count / trendMax) * 100));
                return (
                  <div key={d.day} className="group relative flex-1">
                    <div
                      className="rounded-t bg-primary/70 hover:bg-primary transition-colors"
                      style={{ height: `${h}%`, minHeight: "4px" }}
                      title={`${d.day}: ${d.count} richieste`}
                    />
                    <div className="absolute -top-7 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-popover px-2 py-0.5 text-[10px] shadow-md group-hover:block">
                      {d.day}: <strong>{d.count}</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
            <span>{trendItems[0]?.day}</span>
            <span>{trendItems[trendItems.length - 1]?.day}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Top resources */}
        <Card>
          <CardHeader><CardTitle className="text-base">Top risorse RENTRI</CardTitle></CardHeader>
          <CardContent>
            {topQ.isLoading && <CenteredLoader />}
            {topQ.error && <ErrorState error={topQ.error} />}
            {topQ.data && topQ.data.items.length === 0 && !topQ.error && <EmptyState title="Nessun dato" />}
            <ul className="space-y-2 text-sm">
              {topQ.data?.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Badge variant="outline" className="shrink-0">{it.resource_type}</Badge>
                    <span className="truncate font-mono text-xs">{it.resource_id}</span>
                  </div>
                  <Badge>{it.count}</Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Scadenzario certificati */}
        <Card>
          <CardHeader><CardTitle className="text-base">Scadenzario certificati</CardTitle></CardHeader>
          <CardContent>
            {scadenzeQ.isLoading && <CenteredLoader />}
            {scadenzeQ.error && <ErrorState error={scadenzeQ.error} />}
            {scadenzeQ.data && scadenzeQ.data.items.length === 0 && !scadenzeQ.error && (
              <EmptyState title="Nessun certificato caricato" description="Carica un P12 da Impostazioni" />
            )}
            <ul className="space-y-3 text-sm">
              {scadenzeQ.data?.items.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 rounded border p-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{c.label}</div>
                    <div className="truncate text-xs text-muted-foreground">{c.subject_cn || "—"}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Scade il {c.not_after ? formatDate(c.not_after) : "—"}
                    </div>
                  </div>
                  <ScadenzaBadge days={c.days_to_expire} status={c.status} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string | number; tone: "muted" | "success" | "warning" | "danger" }) {
  const toneCls = {
    muted: "text-muted-foreground",
    success: "text-emerald-600 dark:text-emerald-500",
    warning: "text-amber-600 dark:text-amber-500",
    danger: "text-rose-600 dark:text-rose-500",
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
        </div>
        <div className={toneCls}>{icon}</div>
      </CardContent>
    </Card>
  );
}

function ScadenzaBadge({ days, status }: { days: number; status: "expired" | "warning" | "ok" }) {
  if (status === "expired") return <Badge variant="destructive">Scaduto {Math.abs(days)}g fa</Badge>;
  if (status === "warning") return <Badge variant="secondary" className="bg-amber-500/15 text-amber-700 dark:text-amber-400">{days}g</Badge>;
  return <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">{days}g</Badge>;
}
